<template>
  <nav class="tu-sectiontabs" aria-label="Page sections">
    <div class="tu-sectiontabs__bar" role="tablist">
      <button
        v-for="t in items"
        :key="t.id"
        type="button"
        class="tu-sectiontabs__tab"
        :class="{ 'is-active': active === t.id }"
        role="tab"
        :aria-controls="t.id"
        :aria-selected="active === t.id ? 'true' : 'false'"
        @click="go(t.id)"
      >
        {{ t.label }}
      </button>
    </div>
  </nav>
</template>

<script>
/**
 * Sticky section tabs (scrollspy). Same component as transitionu.health:
 * one scrolling page, the bar sticks under the navbar, a click scrolls to the
 * section and the active pill follows the reader.
 * items: [{ id, label }] where id is an element id on the page.
 */
export default {
  name: 'TuSectionTabs',
  props: {
    items: { type: Array, required: true },
    offset: { type: Number, default: 132 },
  },
  data() {
    return { active: this.items.length ? this.items[0].id : '', ticking: false, locked: false };
  },
  mounted() {
    this.onScroll = () => {
      if (this.ticking || this.locked) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.sync();
        this.ticking = false;
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.$nextTick(this.sync);
  },
  beforeDestroy() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    sync() {
      const line = this.offset + 8;
      let current = this.items.length ? this.items[0].id : '';
      for (const t of this.items) {
        const el = document.getElementById(t.id);
        if (el && el.getBoundingClientRect().top - line <= 0) current = t.id;
      }
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 4) {
        const last = this.items[this.items.length - 1];
        if (last && document.getElementById(last.id)) current = last.id;
      }
      this.active = current;
    },
    go(id) {
      const el = document.getElementById(id);
      if (!el) return;
      this.active = id;
      this.locked = true;
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - this.offset, behavior: 'smooth' });
      window.setTimeout(() => {
        this.locked = false;
        this.sync();
      }, 700);
    },
  },
};
</script>

<style lang="scss" scoped>
.tu-sectiontabs {
  position: sticky;
  top: 64px;
  z-index: 20;
  margin: 24px 0 8px;
  padding: 0;
  background: var(--tu-bg, #fdfdfd);
}
.tu-sectiontabs__bar {
  display: flex;
  gap: 32px;
  max-width: 100%;
  border-bottom: 1px solid var(--tu-line-soft);
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
}
.tu-sectiontabs__tab {
  display: inline-flex;
  align-items: center;
  appearance: none;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  flex: 0 0 auto;
  min-height: 56px;
  padding: 0 2px;
  border-radius: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--tu-ink);
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
  &:hover { color: var(--tu-blue); }
  &.is-active { border-bottom-color: var(--tu-blue); color: var(--tu-blue); }
  &:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: -3px; }
}
@media (max-width: 560px) {
  .tu-sectiontabs__bar { gap: 24px; }
  .tu-sectiontabs__tab { min-height: 48px; font-size: 14px; }
}
@media (prefers-reduced-motion: reduce) { .tu-sectiontabs__tab { transition: none; } }
</style>
